// =============================================================================
// Investor Documents Page
// =============================================================================

import { useEffect, useState } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, DocumentViewerModal } from '../../components/ui';
import { investorService, type DocumentItem } from '../../services/investor.service';
import type { Deal } from '../../types';

// =============================================================================
// Component
// =============================================================================

export function DocumentsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentItem[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<DocumentItem | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDeal, setSelectedDeal] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchQuery, selectedDeal, selectedType]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const docs = await investorService.getDocuments();

      // Extract unique deals from documents
      const uniqueDeals = new Map<string, Deal>();
      docs.forEach(doc => {
        if (doc.deal && !uniqueDeals.has(doc.deal.id)) {
          uniqueDeals.set(doc.deal.id, {
            id: doc.deal.id,
            name: doc.deal.name,
            address: '',
            city: '',
            state: '',
            zipCode: '',
            propertyType: 'OTHER',
            totalEquityRaised: 0,
            isActive: true,
          });
        }
      });

      setDeals(Array.from(uniqueDeals.values()));
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.name.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query) ||
          doc.deal.name.toLowerCase().includes(query)
      );
    }

    // Filter by deal
    if (selectedDeal !== 'all') {
      filtered = filtered.filter((doc) => doc.dealId === selectedDeal);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter((doc) => doc.type === selectedType);
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    setFilteredDocuments(filtered);
  };

  const handleDownload = async (doc: DocumentItem) => {
    setDownloadingId(doc.id);
    try {
      const blob = await investorService.downloadDocument(doc.id);

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      QUARTERLY_REPORT: 'Quarterly Report',
      K1: 'K-1 Tax Document',
      PPM: 'PPM',
      SUBSCRIPTION_AGREEMENT: 'Subscription Agreement',
      FINANCIAL_STATEMENT: 'Financial Statement',
      OPERATING_AGREEMENT: 'Operating Agreement',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDocumentTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      QUARTERLY_REPORT: 'bg-blue-100 text-blue-800',
      K1: 'bg-green-100 text-green-800',
      PPM: 'bg-purple-100 text-purple-800',
      SUBSCRIPTION_AGREEMENT: 'bg-yellow-100 text-yellow-800',
      FINANCIAL_STATEMENT: 'bg-gray-100 text-gray-800',
      OPERATING_AGREEMENT: 'bg-pink-100 text-pink-800',
      OTHER: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const documentTypes = [
    'QUARTERLY_REPORT',
    'K1',
    'PPM',
    'SUBSCRIPTION_AGREEMENT',
    'FINANCIAL_STATEMENT',
    'OPERATING_AGREEMENT',
  ];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
        <p className="mt-1 text-sm text-gray-500">
          Access your investment documents, reports, and tax forms
        </p>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Deal Filter */}
          <div className="w-full md:w-48">
            <select
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Properties</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {deal.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="w-full md:w-48">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              {documentTypes.map((type) => (
                <option key={type} value={type}>
                  {getDocumentTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {filteredDocuments.length} of {documents.length} documents
        </p>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Documents Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery || selectedDeal !== 'all' || selectedType !== 'all'
                ? 'Try adjusting your filters'
                : 'Your documents will appear here once uploaded'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDocuments.map((doc) => (
            <Card key={doc.id} padding="md" hover onClick={() => setViewingDocument(doc)}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="hidden sm:flex p-3 bg-gray-100 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {doc.name}
                      </h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDocumentTypeColor(
                          doc.type
                        )}`}
                      >
                        {getDocumentTypeLabel(doc.type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        {doc.deal.name}
                      </span>
                      <span>-</span>
                      <span>{formatDate(doc.createdAt)}</span>
                    </div>
                    {doc.description && (
                      <p className="mt-1 text-xs text-gray-400 truncate">{doc.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setViewingDocument(doc); }}
                    className="flex items-center gap-2"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">View</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                    disabled={downloadingId === doc.id}
                    className="flex items-center gap-2"
                  >
                    {downloadingId === doc.id ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Stats */}
      <Card padding="md">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Document Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {documents.filter((d) => d.type === 'QUARTERLY_REPORT').length}
            </p>
            <p className="text-xs text-gray-500">Quarterly Reports</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {documents.filter((d) => d.type === 'K1').length}
            </p>
            <p className="text-xs text-gray-500">K-1 Tax Forms</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {documents.filter((d) => d.type === 'SUBSCRIPTION_AGREEMENT').length}
            </p>
            <p className="text-xs text-gray-500">Agreements</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{deals.length}</p>
            <p className="text-xs text-gray-500">Properties</p>
          </div>
        </div>
      </Card>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          getEmbedUrls={investorService.getDocumentViewUrl}
        />
      )}
    </div>
  );
}

export default DocumentsPage;
